import { createTheme } from '@mui/material/styles';
import { theme } from './theme';

export const muiTheme = createTheme({
  spacing: theme.marginBase,
  breakpoints: {
    keys: ['xs', 'sm', 'md', 'lg'],
    values: {
      xs: 0,
      sm: theme.breackPoints.mobile,
      md: theme.breackPoints.tablet,
      lg: 1280,
      xl: 1920,
    },
  },
  direction: 'ltr',
  mixins: {
    toolbar: {
      minHeight: theme.height.regular,
      '@media (min-width:0px) and (orientation: landscape)': {
        minHeight: theme.height.small,
      },
      [theme.media.desktop]: {
        minHeight: theme.height.large,
      },
    },
  },
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          ...theme.fonts.label,
          textTransform: 'none',
          color: theme.colors.inkSecondary,

          marginRight: theme.marginBase * 2,
          padding: `${theme.marginBase}px ${theme.marginBase * 4}px`,
          paddingTop: 0,

          minWidth: 'auto',
          [theme.media.desktop]: {
            minWidth: 'auto',
          },
          [theme.media.tablet]: {
            minWidth: 'auto',
          },
          [theme.media.mobile]: {
            padding: `${theme.marginBase}px ${theme.marginBase * 2}px`,
            minWidth: 'auto',
          },
          '&:last-child': {
            marginRight: 0,
          },
          '&:hover, &:active': {
            color: theme.colors.inkPrimary,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          ...theme.fonts.caption,
          color: theme.colors.inkSecondary,
          '&.Mui-focusVisible': {
            backgroundColor: theme.colors.white,
          },
          '&.Mui-focusVisible:hover': {
            backgroundColor: theme.colors.grayBackground,
          },
          '&.Mui-selected': {
            backgroundColor: theme.colors.white,
            color: theme.colors.accentAccessible,
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          ...theme.fonts.label,
          '&.Mui-focusVisible': {
            border: `1px dotted ${theme.colors.grayBorder}`,
          },
        },
      },
    },
  },
  palette: {
    mode: 'light',
    common: {
      black: theme.colors.inkPrimary,
      white: '#fff',
    },
    primary: {
      light: theme.colors.accentMuted,
      main: theme.colors.accentAccessible,
      dark: theme.colors.accentAccessible,
      contrastText: '#fff',
    },
    secondary: {
      light: theme.colors.blueMuted,
      main: theme.colors.blue,
      dark: theme.colors.blueAccessible,
      contrastText: '#fff',
    },
    error: {
      light: theme.colors.redMuted,
      main: theme.colors.red,
      dark: theme.colors.redAccessible,
      contrastText: '#fff',
    },
    warning: {
      light: theme.colors.yellowMuted,
      main: theme.colors.yellow,
      dark: theme.colors.yellow,
      contrastText: theme.colors.inkPrimary,
    },
    info: {
      light: theme.colors.purpleMuted,
      main: theme.colors.purple,
      dark: theme.colors.purpleAccessible,
      contrastText: '#fff',
    },
    success: {
      light: theme.colors.greenMuted,
      main: theme.colors.green,
      dark: theme.colors.greenAccessible,
      contrastText: '#fff',
    },
    grey: {
      '50': theme.colors.grayBackground,
      '100': theme.colors.grayBackground,
      '200': theme.colors.grayBorder,
      '300': theme.colors.grayBorder,
      '400': theme.colors.inkTertiary,
      '500': theme.colors.inkTertiary,
      '600': theme.colors.inkSecondary,
      '700': theme.colors.inkSecondary,
      '800': theme.colors.inkPrimary,
      '900': theme.colors.inkPrimary,
      A100: theme.colors.grayBackground,
      A200: theme.colors.grayBorder,
      A400: theme.colors.inkPrimary,
      A700: theme.colors.inkSecondary,
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
    text: {
      primary: theme.colors.inkPrimary,
      secondary: theme.colors.inkSecondary,
      disabled: theme.basicColors.inkPrimary.fade(0.3).hex(),
    },
    divider: theme.colors.grayBorder,
    background: {
      paper: '#fff',
      default: theme.colors.grayBackground,
    },
    action: {
      active: 'rgba(0, 0, 0, 0.54)',
      hover: 'rgba(0, 0, 0, 0.04)',
      hoverOpacity: 0.04,
      selected: 'rgba(0, 0, 0, 0.08)',
      selectedOpacity: 0.08,
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
      disabledOpacity: 0.38,
      focus: 'rgba(0, 0, 0, 0.12)',
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
    },
  },
  shadows: [
    'none',
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
    theme.boxShadow.base,
  ],
  typography: {
    fontFamily: theme.fonts.body.fontFamily,
    htmlFontSize: theme.fonts.body.fontSize,
    fontSize: theme.fonts.body.fontSize,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 700,
    fontWeightBold: 900,
    h1: theme.fonts.h1,
    h2: theme.fonts.h2,
    h3: theme.fonts.h3,
    h4: theme.fonts.h4,
    h5: theme.fonts.h5,
    h6: theme.fonts.h5,
    subtitle1: theme.fonts.body,
    subtitle2: theme.fonts.h5,
    body1: theme.fonts.body,
    body2: theme.fonts.caption,
    button: theme.fonts.label,
    caption: theme.fonts.caption,
    overline: theme.fonts.caption,
  },
  shape: {
    borderRadius: theme.borderRadius.std,
  },
});
